import { PAGES_PATH, STEP_NAME_PATH } from "../../navigation/params";
import { useDispatch, useSelector } from "react-redux";
import { useCallback, useEffect, useState } from "react";

import { IRootState } from "../../state";
import { ITokenResponse } from "./user.interface";
import { SIGN_IN } from "./user.mutation";
import { toastMessageActions } from "../../state/request";
import { useMutation } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import useStepNavigation from "../../hooks/useStepNavigation";
import { useUserStatus } from "../user-status/user-status.service";
import { useUserStatusStore } from "../../hooks/redux-hooks/user-status/user-status.store";
import { userActions } from "../../state/user";
import { QUICK_SHARE_UPSELL } from "../../config/params";
import { signOutFirebase } from "../../auth/firebaseAuth";

export const useSignIn = () => {
  const dispatch = useDispatch();
  const { emailSignIn } = useSelector((state: IRootState) => state.user);
  const [userSignIn, { data, loading }] = useMutation<{ signIn: ITokenResponse }>(SIGN_IN);

  const signIn = (password: string) => {
    userSignIn({
      variables: {
        data: {
          email: emailSignIn,
          password,
        },
      },
    });
  };

  useEffect(() => {
    dispatch(toastMessageActions.setGlobalLoading(loading));
  }, [loading]);

  useEffect(() => {
    if (data?.signIn) {
      dispatch(userActions.setToken(data.signIn));
    }
  }, [data]);

  return {
    signInResponse: data?.signIn,
    loading,
    signIn,
  };
};

export const useLogIn = () => {
  const { getUserStatus, userStatus, loading: emailVerificationLoading } = useUserStatus();
  const { signIn, signInResponse, loading: loginLoading } = useSignIn();
  const { setNext } = useStepNavigation();
  const [password, setPassword] = useState("");
  const { emailSignIn } = useUserStatusStore();

  const onLogIn = (email: string, password: string) => {
    setPassword(password);
    if (!userStatus || emailSignIn !== email) {
      getUserStatus(email);
    } else {
      signIn(password);
    }
  };

  useEffect(() => {
    if (!emailVerificationLoading && userStatus) {
      if (
        userStatus.emailExistAuth0 &&
        userStatus.profile?.isEmailConfirmed
      ) {
        if (password) {
          signIn(password);
        }
      } else if (
        userStatus.emailExistAuth0 &&
        userStatus.profile?.isEmailConfirmed === false
      ) {
        setNext(STEP_NAME_PATH.CHECK_INBOX_RETRY);
      } else if (userStatus.emailExistEV) {
        setNext(STEP_NAME_PATH.USER_FOUND_INSTRUCTION);
      } else {
        setNext(STEP_NAME_PATH.DIDNT_FIND_YOU);
      }
    }
  }, [emailVerificationLoading, userStatus]);

  return {
    onLogIn,
    logInResponse: signInResponse,
    loading: emailVerificationLoading || loginLoading,
    userStatus,
    getUserStatus,
  };
};

export const useLogout = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const logout = useCallback(async (redirectToInitialPage: boolean = true) => {
    dispatch({ type: "LOGOUT" });
    localStorage.removeItem("isWebUser");
    localStorage.removeItem("isWebUserApiCalled");
    localStorage.removeItem("wellness-utm"); // to remove for the wellness utm card
    localStorage.removeItem("updateWlnsPlanNtfn"); // to remove for the wellness plan popup time
    localStorage.removeItem("modalsShownForPets"); // to remove wellness renewal modal tracking
    localStorage.removeItem("expressEligibilityShown"); // to remove express eligibility modal
    localStorage.removeItem(QUICK_SHARE_UPSELL); // to remove quich share upsell for customer refferal

    await signOutFirebase();

    if (redirectToInitialPage) {
      navigate(PAGES_PATH.LANDING, { replace: true });
    }
  }, [navigate, dispatch]);

  return {
    logout,
  };
};
